import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, ArrowLeft, Check, History } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';
import { useAuth } from '@/context/AuthContext';
import Mascot from '@/components/Mascot';
import { StepCrop } from '@/components/yield/StepCrop';
import { StepPlace } from '@/components/yield/StepPlace';
import { StepFinish } from '@/components/yield/StepFinish';
import YieldProgress from '@/components/yield/YieldProgress';
import YieldResultPro from '@/components/yield/YieldResultPro';
import { runYieldEstimate } from '@/components/yield/yieldApi';
import { CROPS } from '@/components/yield/yieldConstants';

const STEPS = ['Coltura', 'Posizione', 'Foto e calcolo'];
const num = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));

const INITIAL = {
  crop: 'olivo', variety: '', region: '', regionAuto: false, sizeMode: 'hectares', area_hectares: '', number_of_trees: '',
  lat: '', lng: '', plant_age_years: '', irrigation: '', management: '', pruning: '', planting_density: '', last_year_yield_qha: '',
};

function Stepper({ step, setStep, canGo }) {
  return (
    <div className="flex items-center gap-2" data-testid="yield-stepper">
      {STEPS.map((l, i) => {
        const done = i < step, on = i === step;
        return (
          <button key={l} type="button" onClick={() => canGo(i) && setStep(i)} data-testid={`yield-step-indicator-${i + 1}`}
                  className="flex items-center gap-2 flex-1 min-w-0 group">
            <span className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 transition-colors ${on ? 'bg-chartreuse text-obsidian' : done ? 'bg-chartreuse/20 text-chartreuse' : 'bg-secondary text-muted-foreground'}`}>
              {done ? <Check size={14}/> : i + 1}
            </span>
            <span className={`text-xs truncate hidden sm:block ${on ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{l}</span>
            {i < STEPS.length - 1 && <span className="h-px flex-1 bg-border relative overflow-hidden"><motion.span className="absolute inset-0 bg-chartreuse origin-left" animate={{ scaleX: done ? 1 : 0 }}/></span>}
          </button>
        );
      })}
    </div>
  );
}

export default function StimaQuintali() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [photos, setPhotos] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stages, setStages] = useState({});
  const [retry, setRetry] = useState(null);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const sizeOk = form.sizeMode === 'hectares' ? Number(form.area_hectares) > 0 : Number(form.number_of_trees) > 0;
  const canGo = (i) => i === 0 || sizeOk;
  const savedId = params.get('stima');

  useEffect(() => {
    if (!savedId || !user) return;
    api.get(`/ai/yield-history/${savedId}`).then(({ data }) => {
      setResult(data.result);
      setTimeout(() => document.getElementById('yield-result-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }).catch(() => toast.error('Stima salvata non trovata'));
  }, [savedId, user]);

  const next = () => {
    if (step === 0 && !sizeOk) return toast.error(form.sizeMode === 'hectares' ? 'Inserisci gli ettari' : 'Inserisci il numero di piante');
    setStep(s => Math.min(2, s + 1));
  };

  const submit = async () => {
    if (!sizeOk) { setStep(0); return toast.error('Indica la dimensione del terreno'); }
    setLoading(true); setResult(null); setStages({}); setRetry(null);
    if (savedId) setParams({}, { replace: true });
    setTimeout(() => document.getElementById('yield-result-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    track('yield_estimate_pro_start', { crop: form.crop, has_photos: photos.length });
    const payload = {
      crop: form.crop, variety: form.variety || undefined, region: form.region || undefined,
      area_hectares: form.sizeMode === 'hectares' ? Number(form.area_hectares) : undefined,
      number_of_trees: form.sizeMode === 'trees' ? Number(form.number_of_trees) : undefined,
      lat: num(form.lat), lng: num(form.lng),
      agronomic: {
        plant_age_years: num(form.plant_age_years), irrigation: form.irrigation || undefined, management: form.management || undefined,
        pruning: form.pruning || undefined, planting_density: num(form.planting_density), last_year_yield_qha: num(form.last_year_yield_qha),
      },
      images: photos.map(p => p.b64),
    };
    try {
      const data = await runYieldEstimate(payload, (ev) => {
        if (ev.stage === 'retry') setRetry(ev.detail);
        else setStages(s => ({ ...s, [ev.stage]: { status: ev.status, detail: ev.detail } }));
        if (ev.stage === 'region' && ev.status === 'done' && ev.detail && !form.region) setForm(f => ({ ...f, region: ev.detail === 'non rilevata' ? '' : ev.detail, regionAuto: true }));
      });
      setResult(data);
      track('yield_estimate_pro_success', { crop: form.crop, confidence: data.confidenza });
      if (data.estimate_id) toast.success('Stima salvata nel tuo storico');
    } catch (e) {
      toast.error(e.response ? formatApiError(e) : e.message || 'Stima non riuscita, riprova');
    } finally {
      setLoading(false);
    }
  };

  const cropLabel = CROPS.find(c => c.v === form.crop)?.l;

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden border-b border-border hero-radial">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 md:py-16 relative z-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-chartreuse">YIELD AI PRO · Enterprise edition</div>
          <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            Quanto produrrà il tuo campo? <span className="italic text-chartreuse">3 passaggi, 30 secondi.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
            Rese ISTAT, clima storico, altitudine, agronomia e analisi AI delle foto in un unico report con valore in euro, benchmark regionale e scenari. Gratis.
          </p>
          {user && (
            <Link to="/dashboard?tab=stime" className="mt-5 inline-flex items-center gap-1.5 text-xs text-chartreuse hover:underline" data-testid="yield-history-link">
              <History size={13}/> Le mie stime salvate
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid lg:grid-cols-2 gap-8 [&>*]:min-w-0">
        <div className="rounded-3xl glass-vp p-5 sm:p-7 h-fit" data-testid="yield-wizard">
          <Stepper step={step} setStep={setStep} canGo={canGo}/>
          <div className="mt-7 min-h-[360px]">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
                <h2 className="font-display text-2xl tracking-tight mb-5">
                  {step === 0 ? 'La tua coltura' : step === 1 ? 'Dove si trova?' : 'Ultimo passo'}
                </h2>
                {step === 0 && <StepCrop form={form} set={set}/>}
                {step === 1 && <StepPlace form={form} setForm={setForm}/>}
                {step === 2 && <StepFinish form={form} set={set} photos={photos} setPhotos={setPhotos}/>}
              </motion.div>
          </div>
          {step === 2 && (
            <div className="mt-5 flex flex-wrap gap-1.5 text-[11px]" data-testid="yield-summary">
              {[cropLabel, form.sizeMode === 'hectares' ? `${form.area_hectares} ha` : `${form.number_of_trees} piante`, form.region || (form.lat ? 'posizione indicata' : 'senza posizione'), `${photos.length} foto`].map(t => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">{t}</span>
              ))}
            </div>
          )}
          <div className="mt-6 flex items-center gap-3">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={loading} className="h-12 rounded-full px-5" data-testid="yield-step-back">
                <ArrowLeft size={16} className="mr-1"/> Indietro
              </Button>
            )}
            {step < 2 ? (
              <Button onClick={next} className="flex-1 min-w-0 h-12 rounded-full bg-chartreuse text-obsidian hover:bg-chartreuse/90" data-testid="yield-step-next">
                <span className="truncate">{step === 1 && !form.lat ? <>Continua<span className="hidden sm:inline"> senza posizione</span></> : 'Avanti'}</span> <ArrowRight size={16} className="ml-1 shrink-0"/>
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading} data-testid="yield-submit-btn" className="flex-1 h-12 rounded-full text-base bg-chartreuse text-obsidian hover:bg-chartreuse/90">
                {loading ? <><Sparkles size={16} className="mr-2 animate-spin"/> Analisi in corso…</> : <><Sparkles size={16} className="mr-2"/> Calcola la stima</>}
              </Button>
            )}
          </div>
        </div>

        <div id="yield-result-anchor" className="lg:sticky lg:top-24 h-fit scroll-mt-24">
          {!result && !loading && (
            <div className="rounded-3xl glass-vp p-10 text-center">
              <div className="flex justify-center"><Mascot pose="wave" size={150}/></div>
              <div className="mt-4 font-display text-xl tracking-tight">Il tuo report apparirà qui</div>
              <p className="text-sm text-muted-foreground mt-2">Scegli coltura e dimensione, indica dove si trova e calcola. Foto e dettagli sono facoltativi ma rendono la stima più precisa.</p>
            </div>
          )}
          {loading && <YieldProgress stages={stages} hasPhotos={photos.length > 0} hasPoint={!!form.lat && !form.region} retry={retry}/>}
          {result && !loading && <YieldResultPro result={result}/>}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center border-t border-border">
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter">Ora vendi <span className="italic">al giusto prezzo.</span></h2>
        <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Pubblica la tua partita su AgriNexus con la stima verificata. Contatti diretti, zero commissioni.</p>
        <Link to="/partite/pubblica" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-chartreuse text-obsidian font-medium hover:opacity-90 transition" data-testid="yield-cta-publish">
          Pubblica la tua partita <ArrowRight size={16}/>
        </Link>
      </section>
    </div>
  );
}
